<template>
  <v-row justify="center">
    <v-dialog
      origin="top center"
      v-model="dialogStatus"
      persistent
      scrollable
      content-class="po-received-serial-dialog"
      :max-width="dialogWidth"
    >
      <v-card>
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <span class="font-weight-700">
            Serial Numbers : {{ transactionDetail.product || "Product" }}
            <span v-if="transactionDetail.barcode" class="cyan--text">
              ({{ transactionDetail.barcode }})
            </span>
          </span>
          <v-btn icon small @click="closeDialog">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="px-6 py-4">
          <div class="d-flex align-center justify-end mb-3 px-1">
            <div style="min-width: 280px">
              <v-text-field
                v-model="search"
                clearable
                dense
                outlined
                rounded
                hide-details
                placeholder="Search Serial Number..."
                prepend-inner-icon="mdi-magnify"
                color="cyan"
              ></v-text-field>
            </div>
          </div>

          <div class="po-serial-scroll">
            <v-row class="px-1" v-if="filteredSerials.length">
              <v-col
                v-for="serial in filteredSerials"
                :key="serial.id"
                cols="12"
                sm="6"
                md="3"
                class="pa-2"
              >
                <div class="po-serial-card pa-3">
                  <div class="d-flex align-center justify-space-between">
                    <div class="d-flex align-center text-truncate mr-1">
                      <span class="po-serial-text font-weight-700 text-truncate">
                        {{ getSerialText(serial) }}
                      </span>
                      <v-btn
                        icon
                        x-small
                        class="ml-1"
                        title="Copy Serial Number"
                        @click.stop="copyNumber(getSerialText(serial))"
                      >
                        <v-icon size="14" color="grey darken-1">
                          mdi-content-copy
                        </v-icon>
                      </v-btn>
                    </div>
                    <v-btn
                      icon
                      x-small
                      title="View Barcode"
                      @click.stop="openBarcode(serial)"
                    >
                      <v-icon size="20" color="#0891b2">
                        mdi-qrcode-scan
                      </v-icon>
                    </v-btn>
                  </div>
                </div>
              </v-col>
            </v-row>

            <div v-else class="text-center py-12">
              <v-icon size="48" color="grey lighten-1">mdi-barcode-off</v-icon>
              <p class="mb-0 mt-3 grey--text text--darken-1">
                No serial numbers found.
              </p>
            </div>
          </div>

          <div class="text-center pt-3" v-if="hasMoreResults || loading">
            <v-btn
              v-if="hasMoreResults"
              outlined
              color="cyan darken-1"
              :loading="loading"
              @click="loadMore"
            >
              Load More
            </v-btn>
            <v-progress-circular
              v-else-if="loading"
              indeterminate
              size="24"
              color="cyan"
            ></v-progress-circular>
          </div>
        </v-card-text>

        <v-divider></v-divider>
        <v-card-actions class="px-6 py-3">
          <span class="font-weight-700 font-size-14 custom-header-blue-text">
            Showing {{ serials.length }} of {{ totalRows }} Serial Number(s)
          </span>
          <v-spacer></v-spacer>
          <v-btn
            depressed
            class="custom-grey-border custom-bold-button"
            @click="closeDialog"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog
      origin="top center"
      v-model="barcodeDialog"
      max-width="420"
      persistent
      content-class="barcode-popup-dialog"
    >
      <v-card v-if="selectedSerial">
        <v-card-title
          class="headline dialog-custom-header white-background custom-border-bottom custom-header-blue-text d-flex align-center justify-space-between w-100"
        >
          <span class="font-weight-700">Barcode</span>
          <v-btn icon small @click="barcodeDialog = false">
            <v-icon color="#000">mdi-close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="px-6 py-4">
          <div class="barcode-preview-box pa-6 d-flex flex-column align-center">
            <img
              v-if="selectedSerial.barcode_image"
              :src="selectedSerial.barcode_image"
              class="serial-barcode-image"
              alt="Barcode"
            />
            <v-icon v-else size="96" color="#334155">mdi-qrcode</v-icon>
            <div class="serial-pill-badge mt-4">
              {{ getSerialText(selectedSerial) }}
            </div>
          </div>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="barcodeDialog = false">Close</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import { QUERY } from "@/core/services/store/request.module";
import { SuccessEventBus } from "@/core/lib/message.lib";

export default {
  name: "po-received-serial-dialog",
  props: {
    dialogStatus: {
      type: Boolean,
      default: false,
    },
    purchaseOrder: {
      type: Number,
      required: true,
    },
    transactionDetail: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      search: "",
      serials: [],
      searchTimer: null,
      currentPage: 1,
      totalPages: 1,
      totalRows: 0,
      perPage: 50,
      loading: false,
      barcodeDialog: false,
      selectedSerial: null,
    };
  },
  computed: {
    dialogWidth() {
      return Math.min(
        Math.max((document.body.clientWidth / 100) * 65, 780),
        1200
      );
    },
    filteredSerials() {
      const search = String(this.search || "").toLowerCase().trim();
      if (!search) return this.serials;
      return this.serials.filter((serial) => {
        const serialText = this.getSerialText(serial).toLowerCase();
        return serialText.indexOf(search) !== -1;
      });
    },
    hasMoreResults() {
      return this.currentPage < this.totalPages;
    },
  },
  watch: {
    dialogStatus(isOpen) {
      if (isOpen) {
        this.search = "";
        this.loadSerials(true);
      }
    },
    search(value) {
      clearTimeout(this.searchTimer);
      if (!this.dialogStatus) return;

      this.searchTimer = setTimeout(() => {
        this.loadSerials(true, value || "");
      }, 300);
    },
  },
  methods: {
    loadSerials(reset = false, searchTerm = this.search) {
      if (this.loading) return;
      if (reset) {
        this.serials = [];
        this.currentPage = 1;
        this.totalPages = 1;
        this.totalRows = 0;
      }

      const productId = Number(this.transactionDetail.product_id || 0);
      if (!productId) return;

      this.loading = true;
      this.$store
        .dispatch(QUERY, {
          url: `purchase-order/${this.purchaseOrder}/received-serials`,
          data: {
            product_id: productId,
            product_type: this.getProductType(),
            current_page: this.currentPage,
            per_page: this.perPage,
            search: searchTerm || null,
          },
        })
        .then(({ data }) => {
          const incoming = Array.isArray(data.rows) ? data.rows : [];
          const existingIds = new Set(this.serials.map((serial) => serial.id));
          this.serials = this.serials.concat(
            incoming.filter((serial) => !existingIds.has(serial.id))
          );
          this.currentPage = Number(data.current_page) || this.currentPage;
          this.totalPages = Number(data.total_pages) || 1;
          this.totalRows = Number(data.total_rows) || 0;
        })
        .catch(() => {
          if (reset) {
            this.serials = [];
            this.totalRows = 0;
          }
        })
        .finally(() => {
          this.loading = false;
        });
    },
    loadMore() {
      if (!this.hasMoreResults || this.loading) return;
      this.currentPage += 1;
      this.loadSerials();
    },
    getSerialText(serial) {
      if (!serial) return "";
      return String(
        serial.text ||
          serial.barcode ||
          serial.product_serialize_number?.text ||
          serial.serial_number_id ||
          ""
      ).trim();
    },
    getProductType() {
      return (
        this.transactionDetail.main_product?.product_type ||
        this.transactionDetail.original_product?.product_type ||
        this.transactionDetail.selected_product?.product_type ||
        this.transactionDetail.product_type ||
        null
      );
    },
    copyNumber(value) {
      if (!value || !navigator.clipboard) return;
      navigator.clipboard.writeText(value);
      SuccessEventBus.$emit("update:success", `${value} successfully copied.`);
    },
    openBarcode(serial) {
      this.selectedSerial = serial;
      this.barcodeDialog = true;
    },
    closeDialog() {
      this.$emit("close:dialog");
    },
  },
  mounted() {
    if (this.dialogStatus) this.loadSerials(true);
  },
};
</script>

<style scoped>
.v-dialog__content {
  align-items: start;
}

.po-serial-scroll {
  max-height: 52vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.po-serial-card {
  background: #f4fbf7;
  border: 1.5px solid #d1fae5;
  border-radius: 10px;
  min-height: 58px;
}

.po-serial-card:hover {
  border-color: #059669;
  box-shadow: 0 4px 12px rgba(5, 150, 105, 0.12);
}

.po-serial-text {
  color: #166534;
}

.barcode-preview-box {
  background: #ffffff;
  border: 1.5px dashed #cbd5e1;
  border-radius: 16px;
  min-height: 124px;
}

.serial-barcode-image {
  width: 280px;
  max-width: 100%;
  max-height: 76px;
  object-fit: contain;
  transform: scaleY(1.4);
}

.serial-pill-badge {
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  color: #1d4ed8;
  font-weight: 700;
  font-size: 13px;
  border-radius: 20px;
  padding: 4px 22px;
}
</style>
